/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { OrderDetailsProduct } from '@msdyn365-commerce/components';
import { IActionContext, IGridSettings, IImageSettings } from '@msdyn365-commerce/core';
import {
    ChannelDeliveryOptionConfiguration,
    OrderShipments,
    ProductDeliveryOptions,
    SalesLine,
    SalesOrder,
    Shipment,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';

import { getSalesLine, ISalesLine, ISalesLineResources } from './get-sales-line';
import { SalesLineState } from './sales-line-state';
import { IModuleComponentProps, IPriceContext } from './utilities';

export interface IGetSalesLinesInput {
    moduleName: string;
    moduleComponentProps: IModuleComponentProps;
    isCurrentChannel: boolean;

    salesLines: SalesLine[];
    products: SimpleProduct[];
    shipments?: Shipment[];
    priceContext: IPriceContext;
    resources: ISalesLineResources;
    actionContext: IActionContext;
    gridSettings: IGridSettings;
    imageSettings?: IImageSettings;
    imageOnly?: boolean;
    salesLineStates?: SalesLineState[];
    showCompleteAddress?: boolean;
    showTimeslot: boolean;
    telemetryContent?: ITelemetryContent;
    retailMultiplePickUpOptionEnabled?: boolean;
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
    productDeliveryOptions?: ProductDeliveryOptions[];
    isSelectionModeEnabled?: boolean;
    orderDetailsProducts?: OrderDetailsProduct[];
    isReorderingEnabled?: boolean;
    isChannelMultipleCatalogsFeatureEnabled?: boolean;
}

export const getSalesLines = ({
    moduleName,
    moduleComponentProps,
    salesLines,
    products,
    shipments,
    resources,
    isCurrentChannel,
    imageOnly,
    showCompleteAddress,
    showTimeslot,
    telemetryContent,
    retailMultiplePickUpOptionEnabled,
    channelDeliveryOptionConfig,
    productDeliveryOptions,
    isSelectionModeEnabled,
    orderDetailsProducts,
    isReorderingEnabled,
    salesLineStates: productStates,
    isChannelMultipleCatalogsFeatureEnabled
}: IGetSalesLinesInput): ISalesLine[] => {
    return salesLines.map((salesLine, index) => {
        const product = products.find(simpleProduct => simpleProduct.RecordId === salesLine.ProductId);
        const shipment =
            product &&
            shipments?.find(currentShipment =>
                currentShipment.ShipmentLines?.find(
                    shipmentLine =>
                        shipmentLine.ItemId === product.ItemId && shipmentLine.InventoryTransactionId === salesLine.InventoryTransactionId
                )
            );
        const orderDetailsProduct = isSelectionModeEnabled
            ? product && orderDetailsProducts?.find(currentProduct => currentProduct.salesLine.ProductId === product.RecordId)
            : undefined;
        const productState = productStates?.find(value => value.stateId === `${salesLine.LineId ?? ''}-${index}`);

        return getSalesLine({
            moduleName,
            moduleComponentProps,
            product,
            shipment,
            salesLine,
            isCurrentChannel,
            resources,
            imageOnly,
            showCompleteAddress,
            showTimeslot,
            telemetryContent,
            retailMultiplePickUpOptionEnabled,
            channelDeliveryOptionConfig,
            productDeliveryOptions,
            isSelectionModeEnabled,
            orderDetailsProduct,
            isReorderingEnabled,
            salesLineState: productState,
            isChannelMultipleCatalogsFeatureEnabled
        });
    });
};

interface IProps extends IModuleComponentProps {
    resources: ISalesLineResources;
}

export const getSalesLinesInput = (
    props: IProps,
    moduleName: string,
    order: SalesOrder | OrderShipments,
    products: SimpleProduct[],
    priceContext: IPriceContext,
    showCompleteAddress?: boolean,
    showTimeslot?: boolean,
    telemetryContent?: ITelemetryContent,
    retailMultiplePickUpOptionEnabled?: boolean,
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration,
    productDeliveryOptions?: ProductDeliveryOptions[] | undefined,
    isSelectionModeEnabled?: boolean | undefined,
    orderDetailsProducts?: OrderDetailsProduct[] | undefined,
    isReorderingEnabled?: boolean,
    isChannelMultipleCatalogsFeatureEnabled?: boolean
): IGetSalesLinesInput | undefined => {
    if (!order || !order.SalesLines || order.SalesLines.length === 0) {
        return;
    }

    const {
        resources,
        config: { imageSettings },
        context: {
            actionContext,
            request: {
                gridSettings,
                apiSettings: { channelId }
            }
        }
    } = props;

    const isCurrentChannel = !order.ChannelId || order.ChannelId === channelId;

    return {
        moduleName,
        moduleComponentProps: props,
        isCurrentChannel,
        salesLines: order.SalesLines,
        products,
        shipments: order.Shipments,
        priceContext,
        resources,
        actionContext,
        gridSettings: gridSettings!,
        imageSettings,
        showCompleteAddress,
        showTimeslot: showTimeslot ?? false,
        telemetryContent,
        retailMultiplePickUpOptionEnabled,
        channelDeliveryOptionConfig,
        productDeliveryOptions,
        isSelectionModeEnabled: isSelectionModeEnabled ?? false,
        orderDetailsProducts,
        isReorderingEnabled,
        isChannelMultipleCatalogsFeatureEnabled
    };
};
